import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * SmellWell customisation: the video sources are only attached once the element
 * scrolls close to the viewport, so a background video never costs anything on
 * initial page load.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  observer = null;
  hasLoaded = false;

  connectedCallback() {
    super.connectedCallback();

    if (typeof IntersectionObserver === 'undefined') {
      this.loadSources();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.loadSources();
            this.stopObserving();
            break;
          }
        }
      },
      { rootMargin: '300px 0px' }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (super.disconnectedCallback) super.disconnectedCallback();
    this.stopObserving();
  }

  stopObserving() {
    if (!this.observer) return;
    this.observer.disconnect();
    this.observer = null;
  }

  loadSources() {
    if (this.hasLoaded) return;
    this.hasLoaded = true;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    const attempt = videoElement.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}